import React, { useState } from 'react';
import { Input as AntInput, InputNumber as AntInputNumber } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import styled, { useTheme } from 'styled-components';
import PropTypes from 'prop-types';

const { TextArea: AntTextArea } = AntInput;

/** Focus ring — matches tenant primary (#7543DC), same as Ant Design ConfigProvider. */
const inputFocusCss = (theme) => {
    const primary = theme?.colors?.primary ?? '#7543DC';
    return `
        border-color: ${primary} !important;
        box-shadow: 0 0 0 2px rgba(117, 67, 220, 0.12) !important;
    `;
};

const INPUT_CONFIG = {
    defaultSize: '18px',
    defaultWeight: 400,
    defaultPadding: '8px 12px',
    defaultBorderRadius: '8px',
    defaultBackground: '#F0F0F0',
    defaultColor: '#747474',
    defaultBorder: '#E5E5E5',
    defaultMaxWidth: '300px',

    variant: {
        default: {
            background: '#F0F0F0',
            color: '#747474',
        },
        white: {
            background: '#FFFFFF',
            color: '#000000',
        },
    },

    breakpoints: {
        tablet: {
            maxWidth: '768px',
            sizeMultiplier: 0.89,
            paddingMultiplier: 0.9,
        },
        mobile: {
            maxWidth: '480px',
            sizeMultiplier: 0.89,
            paddingMultiplier: 0.85,
        },
    },
};

/**
 * Input Component
 * Use isPassword={true} for password field (obscured text + eye icon).
 * Use noAutofill={true} to prevent browser autofill (readOnly until focus).
 */
export const Input = React.forwardRef(
    (
        {
            $size = INPUT_CONFIG.defaultSize,
            $weight = INPUT_CONFIG.defaultWeight,
            $padding = INPUT_CONFIG.defaultPadding,
            $borderRadius = INPUT_CONFIG.defaultBorderRadius,
            $background,
            $color,
            $maxWidth = INPUT_CONFIG.defaultMaxWidth,
            $variant = 'default',
            $readOnly = false,
            $type = 'text',
            isPassword = false,
            noAutofill = false,
            ...props
        },
        ref,
    ) => {
        const theme = useTheme();
        const [lockAutofill, setLockAutofill] = useState(!!noAutofill);
        const isDark = theme?.colors?.background?.default === '#141414';
        const variantConfig = INPUT_CONFIG.variant[$variant] || INPUT_CONFIG.variant.default;
        const backgroundColor = $background || (isDark ? '#141414' : variantConfig.background);
        const textColor = $color !== undefined ? $color : undefined;

        const noAutofillAttrs = {
            autoComplete: 'nope',
            'data-lpignore': 'true',
            'data-form-type': 'other',
            'data-1p-ignore': 'true',
            ...(noAutofill && {
                readOnly: lockAutofill,
                onFocus: (e) => {
                    if (lockAutofill) setLockAutofill(false);
                    props.onFocus?.(e);
                },
            }),
        };

        // Password with noAutofill: use type="text" + masked display so browser shows no suggestion dropdown (same as name input)
        if (isPassword && noAutofill) {
            return (
                <PasswordAsTextInput
                    ref={ref}
                    $size={$size}
                    $weight={$weight}
                    $padding={$padding}
                    $borderRadius={$borderRadius}
                    $background={backgroundColor}
                    $color={textColor}
                    $maxWidth={$maxWidth}
                    $readOnly={$readOnly}
                    {...props}
                    {...noAutofillAttrs}
                />
            );
        }

        if (isPassword) {
            return (
                <StyledPassword
                    ref={ref}
                    $size={$size}
                    $weight={$weight}
                    $padding={$padding}
                    $borderRadius={$borderRadius}
                    $background={backgroundColor}
                    $color={textColor}
                    $maxWidth={$maxWidth}
                    $readOnly={$readOnly}
                    {...props}
                    {...noAutofillAttrs}
                    iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                />
            );
        }

        if ($type === 'number') {
            return (
                <StyledInputNumber
                    ref={ref}
                    $size={$size}
                    $weight={$weight}
                    $padding={$padding}
                    $borderRadius={$borderRadius}
                    $background={backgroundColor}
                    $color={textColor}
                    $maxWidth={$maxWidth}
                    readOnly={$readOnly}
                    {...props}
                />
            );
        }

        return (
            <StyledInput
                ref={ref}
                type={noAutofill ? 'text' : $type}
                $size={$size}
                $weight={$weight}
                $padding={$padding}
                $borderRadius={$borderRadius}
                $background={backgroundColor}
                $color={textColor}
                $maxWidth={$maxWidth}
                $readOnly={$readOnly}
                readOnly={$readOnly}
                {...props}
                {...noAutofillAttrs}
            />
        );
    },
);

/**
 * TextArea Component
 * Use $placeholderColor to match Input placeholder (e.g. '#747474'). When not set, defaults to same grey as Input in light mode.
 * Use $weight (default 400) to match Input font-weight.
 */
export const TextArea = ({
    $size = 'clamp(12px, 1.5vw, 18px)',
    $padding = '16px',
    $weight = INPUT_CONFIG.defaultWeight,
    $background,
    $color,
    $placeholderColor,
    $minHeight = '300px',
    ...props
}) => {
    const theme = useTheme();
    const isDark = theme?.colors?.background?.default === '#141414';
    const backgroundColor = $background || (isDark ? '#141414' : '#F5F5F5');
    const textColor = $color || (isDark ? '#FFFFFF' : '#000000');
    const placeholderColor = $placeholderColor ?? (isDark ? '#8A98A7' : INPUT_CONFIG.defaultColor);

    return (
        <StyledTextArea
            $size={$size}
            $padding={$padding}
            $weight={$weight}
            $background={backgroundColor}
            $color={textColor}
            $placeholderColor={placeholderColor}
            $minHeight={$minHeight}
            {...props}
        />
    );
};

const StyledInputNumber = styled(AntInputNumber)`
    width: 100%;
    max-width: ${({ $maxWidth }) => $maxWidth};
    border-radius: ${({ $borderRadius }) => $borderRadius};
    background-color: ${({ $background }) => $background} !important;
    font-family: var(--font-family-geist);
    font-size: ${({ $size }) => $size};
    font-weight: ${({ $weight }) => $weight};
    padding: ${({ $padding }) => $padding};
    height: 40px !important;
    min-height: 40px !important;
    line-height: 40px !important;
    color: ${({ $color, theme }) =>
        $color ?? (theme?.colors?.background?.default === '#141414' ? '#FFFFFF' : '#000000')} !important;

    border: 1px solid ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? '#2C2C2C' : '#E5E5E5')};

    .ant-input-number-input {
        background-color: transparent !important;
        color: inherit !important;
        font-size: inherit;
        font-weight: inherit;
        padding: 0;
        height: 100% !important;
        line-height: 40px !important;
    }

    &:hover {
        border-color: #b8b8b8;
    }

    ${({ theme }) => {
        const focus = inputFocusCss(theme);
        return `
            &:focus-within {
                ${focus}
            }
        `;
    }}
`;

const StyledInput = styled(AntInput)`
    border-radius: ${({ $borderRadius }) => $borderRadius};
    border: 1px solid
        ${({ theme }) => {
        const isDark = theme?.colors?.background?.default === '#141414';
        return isDark ? '#2C2C2C' : INPUT_CONFIG.defaultBorder;
    }};
    font-family: var(--font-family-geist);
    font-size: ${({ $size }) => $size};
    font-weight: ${({ $weight }) => $weight};
    padding: ${({ $padding }) => $padding};
    width: 100%;
    max-width: ${({ $maxWidth }) => $maxWidth};
    height: 40px !important;
    min-height: 40px !important;
    line-height: 40px !important;
    cursor: ${({ $readOnly }) => ($readOnly ? 'default' : 'text')};
    background-color: ${({ $background }) => $background} !important;
    color: ${({ $color, theme }) => {
        if ($color) return $color;
        const isDark = theme?.colors?.background?.default === '#141414';
        return isDark ? '#FFFFFF' : '#000000';
    }} !important;
    transition: all 0.3s ease;

    @media (max-width: ${INPUT_CONFIG.breakpoints.tablet.maxWidth}) {
        max-width: ${({ $maxWidth, $readOnly }) => ($readOnly ? '100%' : $maxWidth)};
        font-size: ${({ $size }) => `calc(${$size} * ${INPUT_CONFIG.breakpoints.tablet.sizeMultiplier})`};
        padding: ${({ $padding }) => {
        const [vertical, horizontal] = $padding.split(' ');
        return `calc(${vertical} * ${INPUT_CONFIG.breakpoints.tablet.paddingMultiplier}) calc(${horizontal} * ${INPUT_CONFIG.breakpoints.tablet.paddingMultiplier})`;
    }};
    }

    @media (max-width: ${INPUT_CONFIG.breakpoints.mobile.maxWidth}) {
        font-size: ${({ $size }) => `calc(${$size} * ${INPUT_CONFIG.breakpoints.mobile.sizeMultiplier})`};
        padding: ${({ $padding }) => {
        const [vertical, horizontal] = $padding.split(' ');
        return `calc(${vertical} * ${INPUT_CONFIG.breakpoints.mobile.paddingMultiplier}) calc(${horizontal} * ${INPUT_CONFIG.breakpoints.mobile.paddingMultiplier})`;
    }};
    }

    &::placeholder {
        color: ${({ $color, theme }) => {
        if ($color) return $color;
        const isDark = theme?.colors?.background?.default === '#141414';
        return isDark ? '#8A98A7' : INPUT_CONFIG.defaultColor;
    }} !important;
    }

    .ant-input {
        background-color: ${({ $background }) => $background} !important;
        color: ${({ $color, theme }) => {
        if ($color) return $color;
        const isDark = theme?.colors?.background?.default === '#141414';
        return isDark ? '#FFFFFF' : '#000000';
    }} !important;
        font-size: ${({ $size }) => $size} !important;
        font-weight: ${({ $weight }) => $weight} !important;
        height: 100% !important;
        line-height: 40px !important;

        @media (max-width: ${INPUT_CONFIG.breakpoints.tablet.maxWidth}) {
            font-size: ${({ $size }) =>
        `calc(${$size} * ${INPUT_CONFIG.breakpoints.tablet.sizeMultiplier}) !important`};
        }

        @media (max-width: ${INPUT_CONFIG.breakpoints.mobile.maxWidth}) {
            font-size: ${({ $size }) =>
        `calc(${$size} * ${INPUT_CONFIG.breakpoints.mobile.sizeMultiplier}) !important`};
        }
    }

    ${({ $readOnly, $background, theme }) => {
        const isDark = theme?.colors?.background?.default === '#141414';
        const defaultBorder = isDark ? '#2C2C2C' : INPUT_CONFIG.defaultBorder;
        const hoverBorder = isDark ? '#404040' : '#B8B8B8';
        const focus = inputFocusCss(theme);

        return $readOnly
            ? `
            &:hover {
                border-color: ${hoverBorder};
                background-color: ${$background} !important;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                transform: translateY(-1px);
            }
            
            &:focus {
                border-color: ${defaultBorder};
                box-shadow: none;
                transform: translateY(0);
                background-color: ${$background} !important;
            }
        `
            : `
            &:hover {
                border-color: ${hoverBorder};
            }
            
            &:focus,
            &:focus-within,
            &.ant-input-affix-wrapper-focused,
            &.ant-input-outlined:focus,
            &.ant-input-outlined-focused,
            &.ant-input-outlined:focus-within {
                ${focus}
            }

            .ant-input:focus {
                ${focus}
            }
        `;
    }}
`;

/* Masked text input – same style as StyledInput, obscures value with dots via CSS (no type="password" = no browser suggestion) */
const StyledInputMasked = styled(StyledInput)`
    .ant-input {
        -webkit-text-security: ${({ $showPassword }) => ($showPassword ? 'none' : 'disc')};
    }
`;

/* Password rendered as text input with masked display – same style as name/email/phone, no browser suggestion dropdown */
const PasswordAsTextInput = React.forwardRef(
    ({ $size, $weight, $padding, $borderRadius, $background, $color, $maxWidth, $readOnly, ...props }, ref) => {
        const [showPassword, setShowPassword] = useState(false);
        const EyeIcon = showPassword ? EyeInvisibleOutlined : EyeOutlined;
        return (
            <StyledInputMasked
                ref={ref}
                type="text"
                $showPassword={showPassword}
                $size={$size}
                $weight={$weight}
                $padding={$padding}
                $borderRadius={$borderRadius}
                $background={$background}
                $color={$color}
                $maxWidth={$maxWidth}
                $readOnly={$readOnly}
                {...props}
                suffix={
                    <EyeIcon
                        role="button"
                        tabIndex={0}
                        onClick={() => setShowPassword((s) => !s)}
                        onKeyDown={(e) => e.key === 'Enter' && setShowPassword((s) => !s)}
                        style={{ cursor: 'pointer', color: 'inherit', opacity: 0.65 }}
                    />
                }
            />
        );
    },
);

PasswordAsTextInput.displayName = 'PasswordAsTextInput';

/* Password input: same look as StyledInput (obscured text + eye icon only) */
const StyledPassword = styled(AntInput.Password)`
    width: 100%;
    max-width: ${({ $maxWidth }) => $maxWidth};
    &.ant-input-password .ant-input-affix-wrapper {
        border-radius: ${({ $borderRadius }) => $borderRadius} !important;
        border: 1px solid
            ${({ theme }) => {
        const isDark = theme?.colors?.background?.default === '#141414';
        return isDark ? '#2C2C2C' : INPUT_CONFIG.defaultBorder;
    }} !important;
        font-family: var(--font-family-geist);
        font-size: ${({ $size }) => $size} !important;
        font-weight: ${({ $weight }) => $weight} !important;
        padding: ${({ $padding }) => $padding} !important;
        height: 40px !important;
        min-height: 40px !important;
        line-height: 40px !important;
        background-color: ${({ $background }) => $background} !important;
        color: ${({ $color, theme }) => {
        if (theme?.colors?.background?.default === '#141414') return '#FFFFFF';
        return $color ?? '#000000';
    }} !important;
        box-shadow: none !important;
    }
    &.ant-input-password .ant-input-affix-wrapper:hover {
        border-color: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? '#404040' : '#B8B8B8'} !important;
    }
    &.ant-input-password .ant-input-affix-wrapper:focus,
    &.ant-input-password .ant-input-affix-wrapper-focused {
        ${({ theme }) => inputFocusCss(theme)}
    }
    &.ant-input-password .ant-input {
        border: none !important;
        padding: 0 !important;
        font-size: inherit !important;
        font-weight: inherit !important;
        background: transparent !important;
        color: inherit !important;
        line-height: 40px !important;
    }
    @media (max-width: ${INPUT_CONFIG.breakpoints.tablet.maxWidth}) {
        .ant-input-affix-wrapper {
            font-size: ${({ $size }) =>
        `calc(${$size} * ${INPUT_CONFIG.breakpoints.tablet.sizeMultiplier}) !important`};
            padding: ${({ $padding }) => {
        const [v, h] = $padding.split(' ');
        return `calc(${v} * ${INPUT_CONFIG.breakpoints.tablet.paddingMultiplier}) calc(${h} * ${INPUT_CONFIG.breakpoints.tablet.paddingMultiplier}) !important`;
    }};
        }
    }
    @media (max-width: ${INPUT_CONFIG.breakpoints.mobile.maxWidth}) {
        .ant-input-affix-wrapper {
            font-size: ${({ $size }) =>
        `calc(${$size} * ${INPUT_CONFIG.breakpoints.mobile.sizeMultiplier}) !important`};
            padding: ${({ $padding }) => {
        const [v, h] = $padding.split(' ');
        return `calc(${v} * ${INPUT_CONFIG.breakpoints.mobile.paddingMultiplier}) calc(${h} * ${INPUT_CONFIG.breakpoints.mobile.paddingMultiplier}) !important`;
    }};
        }
    }
`;

const StyledTextArea = styled(AntTextArea)`
    width: 100%;
    min-height: ${({ $minHeight }) => $minHeight};
    border-radius: 12px;
    border: 1px solid #e5e5e5;
    background-color: ${({ $background }) => $background} !important;
    color: ${({ $color }) => $color} !important;
    font-size: ${({ $size }) => $size} !important;
    font-weight: ${({ $weight }) => $weight} !important;
    font-family: var(--font-family-geist);
    padding: ${({ $padding }) => $padding};
    resize: vertical;
    transition: all 0.2s ease;

    &::placeholder,
    textarea::placeholder,
    .ant-input::placeholder {
        color: ${({ $placeholderColor }) => $placeholderColor} !important;
        font-size: ${({ $size }) => $size} !important;
        font-weight: ${({ $weight }) => $weight} !important;
        font-family: var(--font-family-geist);
    }

    textarea,
    .ant-input {
        font-size: ${({ $size }) => $size} !important;
        font-weight: ${({ $weight }) => $weight} !important;
        font-family: var(--font-family-geist);
        color: inherit;
    }

    @media (max-width: ${INPUT_CONFIG.breakpoints.tablet.maxWidth}) {
        min-height: 250px;
        padding: 12px;
    }

    @media (max-width: ${INPUT_CONFIG.breakpoints.mobile.maxWidth}) {
        min-height: 200px;
        padding: 10px;
    }

    &:hover {
        border-color: #b8b8b8;
    }

    ${({ theme }) => `
        &:focus,
        &:focus-within {
            ${inputFocusCss(theme)}
        }
    `}
`;

Input.displayName = 'Input';

Input.propTypes = {
    $size: PropTypes.string,
    $weight: PropTypes.number,
    $padding: PropTypes.string,
    $borderRadius: PropTypes.string,
    $background: PropTypes.string,
    $color: PropTypes.string,
    $maxWidth: PropTypes.string,
    $variant: PropTypes.oneOf(['default', 'white']),
    $readOnly: PropTypes.bool,
    $type: PropTypes.oneOf(['text', 'number']),
    isPassword: PropTypes.bool,
    noAutofill: PropTypes.bool,
};

TextArea.propTypes = {
    $size: PropTypes.string,
    $padding: PropTypes.string,
    $weight: PropTypes.number,
    $background: PropTypes.string,
    $color: PropTypes.string,
    $placeholderColor: PropTypes.string,
    $minHeight: PropTypes.string,
};

Input.defaultProps = {
    $size: INPUT_CONFIG.defaultSize,
    $weight: INPUT_CONFIG.defaultWeight,
    $padding: INPUT_CONFIG.defaultPadding,
    $borderRadius: INPUT_CONFIG.defaultBorderRadius,
    $maxWidth: INPUT_CONFIG.defaultMaxWidth,
    $variant: 'default',
    $readOnly: false,
    $type: 'text',
    isPassword: false,
    noAutofill: false,
};

TextArea.defaultProps = {
    $size: 'clamp(12px, 1.5vw, 18px)',
    $padding: '16px',
    $minHeight: '300px',
};
